/**
 * Created by Iman on 04/01/2025.
 */

import axios from "axios"
import { useAuthStore } from "@/store/auth"

// Use environment variable or fallback to default value
const API_BASE_URL =
  process.env.VUE_APP_API_BASE_URL ||
  import.meta.env?.VITE_API_BASE_URL ||
  /* "http://beton-market-app.test/api/v1" */
  "https://api.betonmarket.co/api/v1"

const axiosClient = axios.create({
  baseURL: API_BASE_URL,
})

axiosClient.interceptors.request.use((config) => {
  const authStore = useAuthStore()
  const token = authStore.user.token
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  failedQueue = []
}

axiosClient.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            return axiosClient(originalRequest)
          })
          .catch((err) => {
            return Promise.reject(err)
          })
      }

      originalRequest._retry = true
      isRefreshing = true

      const authStore = useAuthStore()
      const refreshToken = authStore.user.refreshToken

      if (refreshToken) {
        try {
          const newToken = await authStore.refreshToken()
          processQueue(null, newToken)
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          return axiosClient(originalRequest)
        } catch (refreshError) {
          processQueue(refreshError, null)
          // For 429 error, don't logout
          // For other errors, logout
          if (refreshError.response?.status !== 429) {
            authStore.forceLogout()
          }
          return Promise.reject(refreshError)
        } finally {
          isRefreshing = false
        }
      } else {
        isRefreshing = false
        authStore.forceLogout()
        return Promise.reject(error)
      }
    }

    return Promise.reject(error)
  }
)

export default axiosClient
